import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/component/navbar';
import PlanCard from '@/components/PlanCard';
import { useSetupPricing } from '@/data/setupPricing';

// Plan cards (titles, copy, features, order) are admin-editable via
// setupPricing/current.setupPlans — see /admin/setup-pricing → Corporate tab.
// Only the routing below is keyed to a plan id: `starter` detours through the
// expedited-formation step, so that id must stay stable.

// Map a marketing-page plan slug (?plan=…) to this flow's plan id.
const SLUG_TO_PLAN: Record<string, string> = {
  'starter': 'starter',
  'total-compliance': 'compliance', 'compliance': 'compliance',
  'business-in-a-box': 'business-in-a-box',
};

export default function SetupPlan() {
  const P = useSetupPricing();
  const [selected, setSelected] = useState<string>(() => {
    const slug = (localStorage.getItem('signup_plan') || '').toLowerCase();
    return SLUG_TO_PLAN[slug] || 'starter';
  });
  const navigate = useNavigate();

  // Consume the marketing plan pre-selection once, so a later manual change sticks.
  useEffect(() => { localStorage.removeItem('signup_plan'); }, []);

  // If the pre-selected/default id isn't one of the configured cards (an admin removed
  // or renamed it), fall back to the first card so the flow can still be completed.
  useEffect(() => {
    if (P.setupPlans.length && !P.setupPlans.some(p => p.id === selected)) {
      setSelected(P.setupPlans[0]!.id);
    }
  }, [P.setupPlans, selected]);

  const handleNext = () => {
    localStorage.setItem('setupPlan', selected);
    if (selected === 'starter') {
      navigate('/setup-expedited');
    } else {
      navigate('/setup-invoice');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Navbar hideSignBtn={false} />
      <div className="flex-1 px-8 py-10 max-w-6xl mx-auto w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-10">
          Choose a plan to power up your business
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {P.setupPlans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              price={P.plans[plan.id]}
              selected={selected === plan.id}
              onSelect={() => setSelected(plan.id)}
            />
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-8 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="px-8 py-3 bg-amber-400 text-gray-900 font-semibold rounded-lg hover:bg-amber-500 transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
